import React, { useEffect, useState } from "react";
import { useCart } from "../utils/CartContext";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../utils/AuthContext";
import * as catalog from "../api/catalog";

export default function CartDrawer({ open, onClose }) {
  const { cart, removeFromCart, updateQuantity, addToCart, toggleSelect, selectAll } = useCart();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState([]);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(0);
  const [addingSuggestionId, setAddingSuggestionId] = useState(null);

  // Calculate selected items
  const selectedItems = cart.filter((it) =>
    typeof it.selected === "undefined" ? true : !!it.selected
  );
  const allSelected = cart.length > 0 && selectedItems.length === cart.length;
  const noneSelected = selectedItems.length === 0;

  // Calculate total for selected items - use cart's priceAtAdd (item.price) which already has discounted price
  const total = cart.reduce((s, it) => {
    const unit =
      it.price ?? it.pricing?.discountedPrice ?? it.pricing?.price ?? 0;
    const qty = it.qty || 1;
    const included = typeof it.selected === "undefined" ? true : !!it.selected;
    return s + (included ? unit * qty : 0);
  }, 0);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let mounted = true;

    const loadSuggestions = async () => {
      try {
        const products = await catalog.listProducts({ limit: 12 });
        const inCartProductIds = new Set(
          (cart || []).map((item) =>
            String(item.product?._id || item.product || ""),
          ),
        );

        const available = (products || []).filter((product) => {
          const productId = String(product?._id || "");
          if (!productId) return false;
          if (inCartProductIds.has(productId)) return false;
          const stock = product?.inventory?.stock ?? product?.stock ?? 0;
          return stock > 0;
        });

        const shuffled = [...available];
        for (let i = shuffled.length - 1; i > 0; i -= 1) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }

        if (mounted) {
          setSuggestions(shuffled.slice(0, 4));
          setActiveSuggestionIndex(0);
        }
      } catch (e) {
        if (mounted) {
          setSuggestions([]);
          setActiveSuggestionIndex(0);
        }
      }
    };

    loadSuggestions();
    return () => {
      mounted = false;
    };
  }, [open, cart]);

  const activeSuggestion = suggestions[activeSuggestionIndex] || null;

  return (
    <div
      className={`fixed inset-0 z-[99999] transition-all duration-300 ${open ? "pointer-events-auto" : "pointer-events-none"}`}
      style={{ zIndex: 99999 }}
    >
      {/* Overlay */}
      <div
        className={`absolute inset-0 bg-black transition-opacity duration-300 ${open ? "opacity-40" : "opacity-0"}`}
        style={{ zIndex: 100000 }}
        onClick={onClose}
      />
      {/* Drawer */}
      <div
        className={`absolute right-0 top-0 h-full w-full max-w-md bg-[#f5f5f5] shadow-xl transform transition-transform duration-300 flex flex-col ${open ? "translate-x-0" : "translate-x-full"}`}
        style={{ minWidth: 340, zIndex: 100001 }}
      >
        <div className="flex items-center justify-between px-7 py-6 border-b border-gray-200">
          <h2
            style={{
              fontSize: 28,
              lineHeight: 1.1,
              fontWeight: 600,
              color: "#101010",
            }}
          >
            Your Cart
          </h2>
          <button
            onClick={onClose}
            className="text-4xl font-normal text-gray-600 hover:text-gray-800 leading-none"
          >
            &times;
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-7 py-5">
          {!user && !loading ? (
            <div className="flex flex-col items-center justify-center h-full gap-4">
              <div className="text-gray-700 text-lg font-semibold">
                Please log in to view your cart.
              </div>
              <button
                className="w-full bg-pink-500 text-white py-3 rounded font-semibold text-base mt-2 hover:bg-pink-600 transition"
                onClick={() => {
                  onClose();
                  navigate("/login");
                }}
              >
                Login
              </button>
            </div>
          ) : cart && cart.length > 0 ? (
            <div className="flex flex-col">
              {/* Select All checkbox */}
              <div className="flex items-center justify-between py-4 border-b border-gray-300 bg-[#f5f5f5]">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={() => selectAll(!allSelected)}
                    className="w-4 h-4 cursor-pointer accent-pink-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Select All ({cart.length} items)
                  </span>
                </label>
                <span className="text-xs text-gray-500">
                  {selectedItems.length} of {cart.length} selected
                </span>
              </div>
              {/* Cart items */}
              <div className="flex flex-col gap-6 pt-6">
              {cart.map((item) => {
                // Use cart's priceAtAdd (item.price) which already has the discounted price
                const unit =
                  item.price ??
                  item.pricing?.discountedPrice ??
                  item.pricing?.price ??
                  0;
                const qty = item.qty || 1;
                const mrp =
                  item.pricing?.compareAtPrice ?? item.pricing?.price ?? unit;
                const discount =
                  mrp > unit ? Math.round(((mrp - unit) / mrp) * 100) : 0;
                const included =
                  typeof item.selected === "undefined" ? true : !!item.selected;
                return (
                  <div
                    key={item._id}
                    className={`flex gap-3 border-b border-gray-200 pb-6 ${included ? "" : "opacity-50"}`}
                  >
                    {/* Checkbox column */}
                    <div className="flex items-start pt-1">
                      <input
                        type="checkbox"
                        checked={included}
                        onChange={() => toggleSelect(item._id)}
                        className="w-4 h-4 cursor-pointer accent-pink-500"
                      />
                    </div>
                    {/* Image */}
                    <div className="w-[88px] h-24 bg-gray-100 flex items-center justify-center shrink-0">
                      {item.photo ? (
                        <Link
                          to={`/product/${item.product?._id || item.product}`}
                        >
                          <img
                            src={item.photo}
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                        </Link>
                      ) : (
                        <div className="w-20 h-20 bg-gray-200" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0 flex flex-col pr-2">
                      <div className="flex items-start justify-between gap-3">
                        <Link
                          to={`/product/${item.product?._id || item.product}`}
                          className="text-gray-900 hover:underline leading-[1.25]"
                          style={{
                            fontSize: 13,
                            letterSpacing: "0.02em",
                            fontWeight: 500,
                          }}
                        >
                          {item.name ||
                            item.productName ||
                            item.basic?.productName}
                        </Link>
                        <button
                          className="text-rose-300 hover:text-rose-500 leading-none mt-0.5"
                          onClick={() => removeFromCart(item._id)}
                          title="Remove"
                          style={{ fontSize: 15 }}
                        >
                          🗑
                        </button>
                      </div>
                      <div
                        className="text-gray-500 mt-1"
                        style={{ fontSize: 10.5, lineHeight: 1.2 }}
                      >
                        {(item.sizes && item.sizes[0]) || "One Size"}
                        {item.colors && item.colors[0]
                          ? ` | ${item.colors[0]}`
                          : ""}
                      </div>

                      <div className="mt-3">
                        <div className="flex items-center gap-1.5">
                          {mrp > unit && (
                            <span
                              className="text-gray-400 line-through"
                              style={{ fontSize: 12 }}
                            >
                              ₹{mrp}
                            </span>
                          )}
                          <span
                            className="text-gray-900 font-semibold"
                            style={{ fontSize: 14 }}
                          >
                            ₹{unit}
                          </span>
                        </div>
                        {discount > 0 && (
                          <div
                            className="text-red-600"
                            style={{ fontSize: 10.5, marginTop: 1 }}
                          >
                            ({discount}% Off)
                          </div>
                        )}
                      </div>

                      <div className="mt-3 flex justify-end">
                        <div className="border border-gray-300 h-9 w-[84px] flex items-center justify-between px-2.5 bg-[#f7f7f7]">
                          <button
                            className="text-gray-700"
                            style={{ fontSize: 15, lineHeight: 1 }}
                            onClick={() =>
                              updateQuantity(item._id, Math.max(1, qty - 1))
                            }
                            aria-label="Decrease quantity"
                          >
                            −
                          </button>
                          <span
                            className="text-gray-900"
                            style={{ fontSize: 12 }}
                          >
                            {qty}
                          </span>
                          <button
                            className="text-gray-700"
                            style={{ fontSize: 15, lineHeight: 1 }}
                            onClick={() =>
                              updateQuantity(item._id, Math.min(10, qty + 1))
                            }
                            aria-label="Increase quantity"
                          >
                            +
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              </div>

              {activeSuggestion && (
                <div className="pt-3">
                  <h3
                    className="text-gray-900 mb-3"
                    style={{ fontSize: 19, fontWeight: 600 }}
                  >
                    Before You Go
                  </h3>
                  <div className="border border-gray-300 p-2.5 flex items-center gap-3 bg-[#f5f5f5]">
                    <Link
                      to={`/product/${activeSuggestion.slug || activeSuggestion._id}`}
                      className="block w-[78px] h-[92px] bg-gray-100 shrink-0"
                    >
                      <img
                        src={
                          activeSuggestion.media?.mainImage ||
                          activeSuggestion.photo ||
                          activeSuggestion.image
                        }
                        alt={
                          activeSuggestion.basic?.productName ||
                          activeSuggestion.name
                        }
                        className="w-full h-full object-cover"
                      />
                    </Link>
                    <div className="flex-1 min-w-0">
                      <Link
                        to={`/product/${activeSuggestion.slug || activeSuggestion._id}`}
                        className="block text-gray-900 truncate"
                        style={{
                          fontSize: 14,
                          lineHeight: 1.2,
                          fontWeight: 500,
                        }}
                      >
                        {activeSuggestion.basic?.productName ||
                          activeSuggestion.name}
                      </Link>
                      <div
                        className="text-gray-500 mt-0.5"
                        style={{ fontSize: 11.5 }}
                      >
                        {(activeSuggestion.sizes &&
                          activeSuggestion.sizes[0]) ||
                          "One Size"}
                        {activeSuggestion.colors && activeSuggestion.colors[0]
                          ? ` | ${activeSuggestion.colors[0]}`
                          : ""}
                      </div>
                      <div className="mt-4 flex items-end justify-between gap-3">
                        <div
                          className="font-semibold text-gray-900"
                          style={{ fontSize: 18 }}
                        >
                          ₹
                          {activeSuggestion.pricing?.discountedPrice ??
                            activeSuggestion.pricing?.price ??
                            activeSuggestion.price ??
                            0}
                        </div>
                        <button
                          className="bg-[#242529] text-white px-4 py-1.5 font-medium min-w-[86px]"
                          style={{ fontSize: 13, height: 40 }}
                          onClick={() => {
                            setAddingSuggestionId(activeSuggestion._id);
                            addToCart(activeSuggestion);
                            setTimeout(() => setAddingSuggestionId(null), 400);
                          }}
                          disabled={addingSuggestionId === activeSuggestion._id}
                        >
                          {addingSuggestionId === activeSuggestion._id
                            ? "ADDING"
                            : "ADD"}
                        </button>
                      </div>
                    </div>
                  </div>

                  {suggestions.length > 1 && (
                    <div className="flex items-center gap-2 mt-3 px-0.5">
                      {suggestions.map((_, index) => (
                        <button
                          key={`suggestion-dot-${index}`}
                          className={`w-2.5 h-2.5 rounded-full ${index === activeSuggestionIndex ? "bg-gray-900" : "bg-gray-300"}`}
                          onClick={() => setActiveSuggestionIndex(index)}
                          aria-label={`Show suggestion ${index + 1}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : user && !loading ? (
            <div className="flex flex-col items-center justify-center h-full gap-4">
              <div className="text-gray-400 text-center mt-12">
                No items in cart.
              </div>
              <button
                className="w-full bg-pink-500 text-white py-3 rounded font-semibold text-base mt-2 hover:bg-pink-600 transition"
                onClick={() => {
                  onClose();
                  navigate("/");
                }}
              >
                Shop now
              </button>
            </div>
          ) : null}
        </div>
        {/* Subtotal and Checkout */}
        {user && cart && cart.length > 0 && (
          <div className="border-t border-gray-200 p-6 bg-[#f5f5f5]">
            <div className="flex justify-between items-center mb-2">
              <span className="text-gray-600 text-sm">
                Subtotal ({selectedItems.length} {selectedItems.length === 1 ? "item" : "items"} selected)
              </span>
              <span className="font-bold text-lg">₹{total}</span>
            </div>
            {noneSelected && (
              <div className="text-red-500 text-xs mb-2">
                Please select at least one item to checkout
              </div>
            )}
            <button
              className={`w-full bg-pink-500 text-white py-3 rounded font-semibold text-base mt-2 transition ${noneSelected ? "opacity-50 cursor-not-allowed" : "hover:bg-pink-600"}`}
              disabled={noneSelected}
              onClick={() => {
                onClose();
                navigate("/checkout");
              }}
            >
              CONTINUE TO CHECKOUT
            </button>
            <div className="text-xs text-gray-500 text-center mt-3">
              Almost yours. Finish checkout to lock it in.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
